import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Mail, Plus, Gift, ShoppingBag, Wallet, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { NumberInput } from '../components/ui/number-input';

const TYPE_META = {
  organic: { label: 'organic mailer', Icon: Gift, tint: 'bg-pink-50 text-pink-700 border-pink-200' },
  affiliate: { label: 'tts affiliate', Icon: ShoppingBag, tint: 'bg-lilac-50 text-lilac-700 border-lilac-200' },
  paid: { label: 'paid campaign', Icon: Wallet, tint: 'bg-amber-50 text-amber-700 border-amber-200' },
};
const STATUS_META = {
  draft: { label: 'draft', dot: '#94a3b8' },
  active: { label: 'active', dot: '#10b981' },
  completed: { label: 'completed', dot: '#a78bfa' },
};

const emptyForm = () => ({ client_id: '', name: '', type: 'organic', creator_count: 5 });
const fmtShort = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const Mailers = () => {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [clientFilter, setClientFilter] = useState('all');

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

  const filterClients = useMemo(() => {
    const ids = [...new Set(campaigns.map((c) => c.client_id))];
    return ids.map((id) => clientMap[id]).filter(Boolean).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [campaigns, clientMap]);

  const visibleCampaigns = useMemo(
    () => (clientFilter === 'all' ? campaigns : campaigns.filter((c) => c.client_id === clientFilter)),
    [campaigns, clientFilter]
  );

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cl }, { data: camp }] = await Promise.all([api.get('/clients'), api.get('/mailers/campaigns')]);
        setClients(cl || []);
        setCampaigns(camp || []);
      } catch { toast.error('failed to load mailers'); }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const deleteCampaign = async (e, campaignId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('delete this whole mailer campaign? this removes all its creators too.')) return;
    try {
      await api.delete(`/mailers/campaigns/${campaignId}`);
      setCampaigns((cs) => cs.filter((c) => c.id !== campaignId));
      toast.success('campaign deleted');
    } catch { toast.error('failed to delete campaign'); }
  };

  const createCampaign = async () => {
    if (!form.client_id || !(form.name || '').trim()) { toast.error('pick a client and name the campaign'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/mailers/campaigns', {
        client_id: form.client_id,
        name: form.name.trim(),
        type: form.type,
        status: 'draft',
        creator_count: form.type !== 'affiliate' ? Number(form.creator_count) || 0 : 0,
      });
      toast.success('campaign created');
      setOpen(false);
      setForm(emptyForm());
      nav(`/mailers/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed to create campaign'); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-8" data-emoji-skip="true">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <Mail className="w-6 h-6 text-pink-500" /> mailers
          </h1>
          <p className="text-sm text-slate-500 mt-1">gifting & affiliate creator seeding — track outreach to content, per client.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-pink-500 hover:bg-pink-600 text-white text-xs h-9" data-testid="new-mailer-campaign-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> new campaign
        </Button>
      </header>

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">loading…</div>
      ) : campaigns.length === 0 ? (
        <div className="bg-gradient-to-br from-pink-50 to-white border border-dashed border-pink-200 rounded-2xl p-12 text-center text-sm text-slate-400" data-testid="mailers-empty-state">
          no mailer campaigns yet — create one to start seeding creators
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-5" data-testid="mailer-client-filters">
            <button
              type="button"
              onClick={() => setClientFilter('all')}
              className={`px-3.5 h-8 rounded-full text-xs font-medium border transition ${clientFilter === 'all' ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-600 border-slate-200 hover:border-pink-300'}`}
              data-testid="mailer-client-filter-all"
            >
              all
            </button>
            {filterClients.map((cl) => (
              <button
                key={cl.id}
                type="button"
                onClick={() => setClientFilter(cl.id)}
                className={`px-3.5 h-8 rounded-full text-xs font-medium border inline-flex items-center gap-1.5 transition ${clientFilter === cl.id ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-600 border-slate-200 hover:border-pink-300'}`}
                data-testid={`mailer-client-filter-${cl.id}`}
              >
                <span>{cl.logo}</span>{cl.name}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-3">
          {visibleCampaigns.map((c) => {
            const cl = clientMap[c.client_id];
            const t = TYPE_META[c.type] || TYPE_META.organic;
            const s = STATUS_META[c.status] || STATUS_META.draft;
            return (
              <Link key={c.id} to={`/mailers/${c.id}`} className="block relative bg-gradient-to-br from-pink-50 to-white border border-pink-200 rounded-2xl p-4 hover:border-pink-400 hover:shadow-md transition" data-testid={`mailer-campaign-${c.id}`}>
                <button
                  onClick={(e) => deleteCampaign(e, c.id)}
                  className="absolute top-3 right-3 p-1 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-md"
                  title="delete campaign"
                  data-testid={`delete-mailer-campaign-${c.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <div className="flex items-center justify-between mb-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${t.tint}`}><t.Icon className="w-3 h-3" /> {t.label}</span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />{s.label}</span>
                </div>
                <div className="font-semibold text-slate-800 truncate">{c.name}</div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                  <span>{cl?.logo}</span><span className="truncate">{cl?.name || 'unknown client'}</span>
                </div>
                {(c.start_date || c.end_date) && (
                  <div className="text-[11px] text-pink-600 mt-2 pt-2 border-t border-pink-100">
                    {c.start_date ? fmtShort(c.start_date) : '—'} → {c.end_date ? fmtShort(c.end_date) : '—'}
                  </div>
                )}
              </Link>
            );
          })}
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>new mailer campaign</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-slate-500">client</label>
              <Select value={form.client_id} onValueChange={(v) => set({ client_id: v })}>
                <SelectTrigger className="mt-1" data-testid="mailer-client-select"><SelectValue placeholder="select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.logo} {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500">campaign name</label>
              <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. summer gifting wave 1" className="mt-1" data-testid="mailer-name-input" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500">type</label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                <button type="button" onClick={() => set({ type: 'organic' })} className={`h-10 rounded-lg text-xs font-medium border inline-flex items-center justify-center gap-1.5 ${form.type === 'organic' ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-600 border-slate-200'}`} data-testid="mailer-type-organic"><Gift className="w-3.5 h-3.5" /> organic mailer</button>
                <button type="button" onClick={() => set({ type: 'affiliate' })} className={`h-10 rounded-lg text-xs font-medium border inline-flex items-center justify-center gap-1.5 ${form.type === 'affiliate' ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-600 border-slate-200'}`} data-testid="mailer-type-affiliate"><ShoppingBag className="w-3.5 h-3.5" /> tts affiliate</button>
                <button type="button" onClick={() => set({ type: 'paid' })} className={`h-10 rounded-lg text-xs font-medium border inline-flex items-center justify-center gap-1.5 ${form.type === 'paid' ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-600 border-slate-200'}`} data-testid="mailer-type-paid"><Wallet className="w-3.5 h-3.5" /> paid campaign</button>
              </div>
            </div>
            {form.type !== 'affiliate' && (
              <div>
                <label className="text-[11px] text-slate-500">how many creators do you need?</label>
                <NumberInput value={form.creator_count} onChange={(v) => set({ creator_count: v })} className="mt-1 w-28" data-testid="mailer-creator-count-input" />
                <p className="text-[10px] text-slate-400 mt-1">we'll create this many blank rows for you to fill in</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-xs h-9">cancel</Button>
            <Button onClick={createCampaign} disabled={saving} className="bg-pink-500 hover:bg-pink-600 text-white text-xs h-9" data-testid="create-mailer-campaign-btn">{saving ? 'creating…' : 'create campaign'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Mailers;
